import { FEMA_ENVIRONMENT_OPTIONS } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/femaEnvironment.config';
import type {
  FemaCredentialSlotName,
  FemaEnvironmentClass,
  FemaExpiryBand,
  FemaTenantRefusal,
  FemaTenantSummary,
} from 'types/femaTenant.types';
import {
  FEMA_CLASS_BADGE_VARIANTS,
  FEMA_CREDENTIAL_NONE_DETAIL,
  FEMA_CREDENTIAL_STATE_PRESENTATION,
  FEMA_EXPIRY_BAND_VARIANTS,
  FEMA_EXPIRY_POLICY_SENTENCE,
  FEMA_EXPIRY_RENEW_SENTENCE,
  FEMA_EXPIRY_WARNING_DAYS,
  FEMA_NOT_RECORDED,
  type FemaCredentialAction,
  type FemaCredentialVariant,
} from '../config/femaCredentials.config';

/**
 * Contract §3 `expiryBandOf`: `null` → `null`; `< 0` → `expired`; `<= 7` → `'7'`; `<= 14` →
 * `'14'`; `<= 30` → `'30'`; `<= 60` → `'60'`; `<= 90` → `'90'`; else `ok`. The page uses it only
 * when the backend sends no `expiryBand`.
 */
export const expiryBandOf = (daysToExpiry: number | null): FemaExpiryBand | null => {
  if (daysToExpiry === null || !Number.isFinite(daysToExpiry)) return null;
  if (daysToExpiry < 0) return 'expired';
  const thresholds = [...FEMA_EXPIRY_WARNING_DAYS].sort((a, b) => a - b);
  const band = thresholds.find((threshold) => daysToExpiry <= threshold);
  return band === undefined ? 'ok' : (String(band) as FemaExpiryBand);
};

/** The band the backend sent, else the band from `daysToExpiry`. */
export const tenantExpiryBand = (tenant: FemaTenantSummary): FemaExpiryBand | null =>
  tenant.expiryBand ?? expiryBandOf(tenant.daysToExpiry);

/** An ISO date as `YYYY-MM-DD` (UTC). A missing or bad value reads "Not recorded". */
export const formatCredentialDate = (value: string | null | undefined): string => {
  if (!value) return FEMA_NOT_RECORDED;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? FEMA_NOT_RECORDED : date.toISOString().slice(0, 10);
};

/** The display label of a class: `TDL / Staging`, `Test / Demo`, `LIVE PRODUCTION`. */
export const environmentClassLabel = (environment: FemaEnvironmentClass): string =>
  FEMA_ENVIRONMENT_OPTIONS.find((option) => option.value === environment)?.credentialLabel ??
  environment;

export const environmentClassBadgeVariant = (
  environment: FemaEnvironmentClass,
): FemaCredentialVariant => FEMA_CLASS_BADGE_VARIANTS[environment];

const dayWord = (days: number): string => (Math.abs(days) === 1 ? 'day' : 'days');

export type CredentialStateDescription = {
  headline: string;
  detail: string | null;
  variant: FemaCredentialVariant;
  actions: FemaCredentialAction[];
};

const headlineFor = (tenant: FemaTenantSummary): string => {
  const notAfter = formatCredentialDate(tenant.active?.notAfter);
  const days = tenant.daysToExpiry;
  switch (tenant.credentialState) {
    case 'none':
      return 'No credential. You cannot send.';
    case 'stored':
      return 'Stored. Not validated. You cannot send.';
    case 'validated':
      return 'Validated. Not enabled yet.';
    case 'active':
      return days === null
        ? `Active. Expires ${notAfter}.`
        : `Active. Expires ${notAfter} (${days} ${dayWord(days)}).`;
    case 'expiring':
      return days === null
        ? `Active. Expires ${notAfter}.`
        : `Active. Expires in ${days} ${dayWord(days)}.`;
    case 'expired':
      return `OUTAGE: the certificate expired on ${notAfter}. You cannot send.`;
    case 'failed':
      return `The credential does not load. Stage: ${tenant.failedStage ?? FEMA_NOT_RECORDED}.`;
    case 'retired':
      return 'Retired. You cannot send.';
  }
};

/**
 * Design read §3.3: the headline, variant and actions of ONE status card. A headline never
 * carries a value from the credential; a failure names the stage only.
 */
export const describeCredentialState = (tenant: FemaTenantSummary): CredentialStateDescription => {
  const presentation = FEMA_CREDENTIAL_STATE_PRESENTATION[tenant.credentialState];
  const band = tenantExpiryBand(tenant);
  const variant =
    tenant.credentialState === 'expiring' && (band === '14' || band === '7')
      ? 'danger'
      : presentation.variant;

  const actions: FemaCredentialAction[] = [...presentation.actions];
  if (tenant.replacement && tenant.credentialState !== 'retired') {
    actions.push(tenant.replacement.validatedAt ? 'rotate' : 'validateReplacement');
  }

  return {
    headline: headlineFor(tenant),
    detail: tenant.credentialState === 'none' ? FEMA_CREDENTIAL_NONE_DETAIL : null,
    variant,
    actions,
  };
};

export type ExpiryBannerDescription = {
  variant: FemaCredentialVariant;
  message: string;
  policy: string;
};

/**
 * Design read §3.6: the expiry banner of ONE class. It shows at 90, 60, 30, 14 and 7 days and
 * after the expiry. It shows nothing for `ok`, for no date, and for a retired tenant.
 */
export const describeExpiryBanner = (tenant: FemaTenantSummary): ExpiryBannerDescription | null => {
  if (tenant.credentialState === 'retired' || tenant.credentialState === 'none') return null;
  const band = tenant.credentialState === 'expired' ? 'expired' : tenantExpiryBand(tenant);
  if (band === null || band === 'ok') return null;

  const label = environmentClassLabel(tenant.environment);
  const days = tenant.daysToExpiry;
  const when = days === null ? `within ${band} days` : `in ${days} ${dayWord(days)}`;
  const message =
    band === 'expired'
      ? `OUTAGE: your FEMA certificate for ${label} expired on ${formatCredentialDate(tenant.active?.notAfter)}. You cannot send. ${FEMA_EXPIRY_RENEW_SENTENCE}`
      : `Your FEMA certificate for ${label} expires ${when}. ${FEMA_EXPIRY_RENEW_SENTENCE}`;

  return { variant: FEMA_EXPIRY_BAND_VARIANTS[band], message, policy: FEMA_EXPIRY_POLICY_SENTENCE };
};

/**
 * The text of ONE refusal: the `code` name, the `stage` name when there is one, then the message.
 * The caller renders it as a React text child only, never as HTML.
 */
export const describeFemaTenantRefusal = (refusal: FemaTenantRefusal): string =>
  `Refused: ${refusal.code}.${refusal.stage ? ` Stage: ${refusal.stage}.` : ''} ${refusal.message}`;

/** The slot a Validate action checks: the replacement while it waits, else the active slot. */
export const validateSlotFor = (tenant: FemaTenantSummary): FemaCredentialSlotName =>
  tenant.replacement && !tenant.replacement.validatedAt ? 'pending' : 'active';
